import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';
export type Skin = 'default' | 'bordered';
export type Layout = 'vertical' | 'collapsed' | 'horizontal';
export type ContentWidth = 'compact' | 'wide';
export type Direction = 'ltr' | 'rtl';

export interface ThemeSettings {
  mode: ThemeMode;
  primaryColor: string;
  skin: Skin;
  layout: Layout;
  contentWidth: ContentWidth;
  direction: Direction;
  semiDarkMenu: boolean;
}

const DEFAULT_SETTINGS: ThemeSettings = {
  mode: 'dark',
  primaryColor: '#9155fd', // Materio default purple
  skin: 'default',
  layout: 'vertical',
  contentWidth: 'wide',
  direction: 'ltr',
  semiDarkMenu: false,
};

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly mediaQueryList: MediaQueryList | null = null;
  private readonly onSystemThemeChange = (): void => {
    if (this.mode() === 'system') {
      this.applyAll();
    }
  };

  readonly mode = signal<ThemeMode>(DEFAULT_SETTINGS.mode);
  readonly primaryColor = signal<string>(DEFAULT_SETTINGS.primaryColor);
  readonly skin = signal<Skin>(DEFAULT_SETTINGS.skin);
  readonly layout = signal<Layout>(DEFAULT_SETTINGS.layout);
  readonly contentWidth = signal<ContentWidth>(DEFAULT_SETTINGS.contentWidth);
  readonly direction = signal<Direction>(DEFAULT_SETTINGS.direction);
  readonly semiDarkMenu = signal<boolean>(DEFAULT_SETTINGS.semiDarkMenu);
  readonly isOpen = signal(false);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.mediaQueryList = globalThis.matchMedia('(prefers-color-scheme: dark)');
      this.mediaQueryList.addEventListener('change', this.onSystemThemeChange);
      this.loadSettings();
      this.applyAll();
    }
  }

  setMode(val: ThemeMode) {
    this.mode.set(val);
    this.saveAndApply();
  }
  setPrimaryColor(val: string) {
    this.primaryColor.set(val);
    this.saveAndApply();
  }
  setSkin(val: Skin) {
    this.skin.set(val);
    this.saveAndApply();
  }
  setLayout(val: Layout) {
    this.layout.set(val);
    this.saveAndApply();
  }
  setContentWidth(val: ContentWidth) {
    this.contentWidth.set(val);
    this.saveAndApply();
  }
  setDirection(val: Direction) {
    this.direction.set(val);
    this.saveAndApply();
  }
  setSemiDarkMenu(val: boolean) {
    this.semiDarkMenu.set(val);
    this.saveAndApply();
  }

  reset() {
    this.mode.set(DEFAULT_SETTINGS.mode);
    this.primaryColor.set(DEFAULT_SETTINGS.primaryColor);
    this.skin.set(DEFAULT_SETTINGS.skin);
    this.layout.set(DEFAULT_SETTINGS.layout);
    this.contentWidth.set(DEFAULT_SETTINGS.contentWidth);
    this.direction.set(DEFAULT_SETTINGS.direction);
    this.semiDarkMenu.set(DEFAULT_SETTINGS.semiDarkMenu);
    this.saveAndApply();
  }

  toggleCustomizer() {
    this.isOpen.set(!this.isOpen());
  }

  private saveAndApply() {
    if (!isPlatformBrowser(this.platformId)) return;

    const settings: ThemeSettings = {
      mode: this.mode(),
      primaryColor: this.primaryColor(),
      skin: this.skin(),
      layout: this.layout(),
      contentWidth: this.contentWidth(),
      direction: this.direction(),
      semiDarkMenu: this.semiDarkMenu(),
    };

    localStorage.setItem('crm-theme-settings', JSON.stringify(settings));
    this.applyAll();
  }

  private loadSettings() {
    const saved = localStorage.getItem('crm-theme-settings');
    if (!saved) return;
    try {
      const settings = JSON.parse(saved) as ThemeSettings;
      if (settings.mode) this.mode.set(settings.mode);
      if (settings.primaryColor) this.primaryColor.set(settings.primaryColor);
      if (settings.skin) this.skin.set(settings.skin);
      if (settings.layout) this.layout.set(settings.layout);
      if (settings.contentWidth) this.contentWidth.set(settings.contentWidth);
      if (settings.direction) this.direction.set(settings.direction);
      if (settings.semiDarkMenu !== undefined) this.semiDarkMenu.set(settings.semiDarkMenu);
    } catch {
      // ignore invalid saved settings
    }
  }

  private applyAll() {
    if (!isPlatformBrowser(this.platformId)) return;
    const html = document.documentElement;
    const effectiveMode = this.resolveThemeMode();
    html.dataset['themeMode'] = effectiveMode;
    html.dataset['themePreference'] = this.mode();
    html.dataset['primaryColor'] = this.primaryColor();
    html.dataset['skin'] = this.skin();
    html.dataset['layout'] = this.layout();
    html.dataset['contentWidth'] = this.contentWidth();
    html.dir = this.direction();
    html.classList.toggle('semi-dark-menu', this.semiDarkMenu());

    this.updatePrimaryVariants(this.primaryColor());
  }

  private resolveThemeMode(): 'light' | 'dark' {
    const preference = this.mode();
    if (preference === 'system') {
      const mediaQuery =
        this.mediaQueryList ?? globalThis.matchMedia('(prefers-color-scheme: dark)');
      return mediaQuery.matches ? 'dark' : 'light';
    }
    return preference;
  }

  private updatePrimaryVariants(color: string) {
    if (!color.startsWith('#')) {
      return;
    }

    const html = document.documentElement;
    html.style.setProperty('--primary', color);
    html.style.setProperty('--primary-hover', this.adjustBrightness(color, -15));
    html.style.setProperty('--primary-light', `${color}1A`);
    html.style.setProperty('--primary-text', color);
  }

  private clampColorChannel(value: number): number {
    if (value < 1) return 0;
    if (value > 255) return 255;
    return value;
  }

  private adjustBrightness(hex: string, percent: number): string {
    const num = Number.parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const r = this.clampColorChannel((num >> 16) + amt);
    const g = this.clampColorChannel(((num >> 8) & 0x00ff) + amt);
    const b = this.clampColorChannel((num & 0x0000ff) + amt);
    return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`;
  }
}
